// frontend/src/components/leads/LeadList.js

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import Layout from "../common/Layout";
import LeadTable from "./LeadTable";
import LeadFilters from "./LeadFilters";
import ViewLead from "./ViewLead";
import BulkImport from "../import/BulkImport";
import ExportButton from "../export/ExportButton";
import LoadingSpinner from "../common/LoadingSpinner";
import "../../styles/LeadPanel.css";

const PAGE_SIZE = 30;

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "New", label: "New" },
  { key: "Contacted", label: "Contacted" },
  { key: "Qualified", label: "Qualified" },
  { key: "Demo Scheduled", label: "Demo Scheduled" },
  { key: "Demo Completed", label: "Demo Completed" },
  { key: "Won", label: "Won" },
  { key: "Lost", label: "Lost" },
];

const DEFAULT_COLUMNS = {
  dealership: true,
  salesRep: true,
  contact: true,
  status: true,
  products: true,
  dms: true,
  demo: true,
};

const OWNER_ROLES = ["Admin", "SM", "Sales Owner", "Sales Manager"];

function LeadList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userRole = user?.role || "";
  const ownerCanFilterSalesRep = OWNER_ROLES.includes(userRole);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [countsError, setCountsError] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [showBulkImport, setShowBulkImport] = useState(false);
  // Removed showColumns state - no longer needed
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const requestIdRef = useRef(0);

  // Product catalog for modal categories
  const [productCatalog, setProductCatalog] = useState([]);

  const [filters, setFilters] = useState({
    status: "all",
    search: "",
    category: "",
    products: [],
    dmsProviders: [],
    leadType: "all",
    leadSource: "all",
    serviceDashboard: "all",
    salesRep: "all",
  });

  const [counts, setCounts] = useState({
    total: 0,
    new_count: 0,
    contacted_count: 0,
    qualified_count: 0,
    demo_scheduled_count: 0,
    demo_completed_count: 0,
    won_count: 0,
    lost_count: 0,
  });

  // Load product catalog for LeadTable modal
  useEffect(() => {
    api
      .getMasterData("products")
      .then((data) => {
        setProductCatalog(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error("Failed to load product catalog:", err));
  }, []);

  const fetchLeads = useCallback(
    async (nextFilters = filters, nextPage = 1, append = false) => {
      const requestId = ++requestIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const result = await api.getPaginatedLeads(
          nextFilters,
          nextPage,
          PAGE_SIZE,
        );
        if (requestId !== requestIdRef.current) return;

        const rows = Array.isArray(result?.data) ? result.data : [];
        setLeads((current) => {
          if (!append) return rows;
          const existing = new Set(current.map((lead) => String(lead.lead_id)));
          return [
            ...current,
            ...rows.filter((lead) => !existing.has(String(lead.lead_id))),
          ];
        });
        setPage(Number(result?.page) || nextPage);
        setHasMore(Boolean(result?.hasMore));
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load leads:", err);
        setError(err?.response?.data?.error || "Failed to load leads");
        if (!append) setLeads([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [filters],
  );

  const fetchCounts = useCallback(
    async (nextFilters = filters) => {
      setCountsLoading(true);
      setCountsError(null);
      try {
        const result = await api.getLeadStateCounts(nextFilters);
        setCounts({
          total: Number(result?.total) || 0,
          new_count: Number(result?.new_count) || 0,
          contacted_count: Number(result?.contacted_count) || 0,
          qualified_count: Number(result?.qualified_count) || 0,
          demo_scheduled_count: Number(result?.demo_scheduled_count) || 0,
          demo_completed_count: Number(result?.demo_completed_count) || 0,
          won_count: Number(result?.won_count) || 0,
          lost_count: Number(result?.lost_count) || 0,
        });
      } catch (err) {
        console.error("Failed to load lead counts:", err);
        setCountsError(
          err?.response?.data?.error || "Failed to load lead counts",
        );
      } finally {
        setCountsLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    setPage(1);
    setHasMore(false);
    fetchLeads(filters, 1, false);
    fetchCounts(filters);
  }, [filters, fetchLeads, fetchCounts]);

  useEffect(() => {
    const openLeadId = location.state?.openLeadId;
    if (openLeadId) {
      setSelectedLeadId(openLeadId);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state]);

  const handleFilterChange = (newFilters) => {
    setFilters((current) => ({ ...current, ...newFilters }));
  };

  const handleStatusTab = (status) => {
    setFilters((current) => ({ ...current, status }));
  };

  const loadMore = () => {
    if (!loading && !loadingMore && hasMore) {
      fetchLeads(filters, page + 1, true);
    }
  };

  const handleViewLead = (leadId) => setSelectedLeadId(leadId);
  const handleEditLead = (leadId) => navigate(`/leads/${leadId}/edit`);
  const handleBookDemo = (leadId) => navigate(`/leads/${leadId}/demo`);

  const handleLeadUpdated = async () => {
    await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
  };
  const handleAddActivity = async (leadId, activityData) => {
    const payload = {
      leadId: Number(leadId),
      type: activityData.type,
      description: activityData.description.trim(),
      scheduledDate: activityData.scheduledDate || new Date().toISOString(),
    };

    await api.addActivity(payload);

    await Promise.all([fetchLeads(filters, page, false), fetchCounts(filters)]);
  };

  const handleLeadQuickUpdated = (leadId, newStatus) => {
    setLeads((currentLeads) => {
      const updatedLeads = currentLeads.map((lead) =>
        String(lead.lead_id) === String(leadId)
          ? { ...lead, status: newStatus }
          : lead,
      );

      // If we're viewing a specific status tab, remove the lead
      // from the current list when its new status no longer matches.
      if (filters.status !== "all") {
        return updatedLeads.filter(
          (lead) =>
            String(lead.lead_id) !== String(leadId) ||
            lead.status === filters.status,
        );
      }

      return updatedLeads;
    });

    // Keep the status counts in sync immediately.
    setCounts((currentCounts) => {
      const lead = leads.find(
        (item) => String(item.lead_id) === String(leadId),
      );

      if (!lead || lead.status === newStatus) {
        return currentCounts;
      }

      const statusCountKeys = {
        New: "new_count",
        Contacted: "contacted_count",
        Qualified: "qualified_count",
        "Demo Scheduled": "demo_scheduled_count",
        "Demo Completed": "demo_completed_count",
        Won: "won_count",
        Lost: "lost_count",
      };

      const oldKey = statusCountKeys[lead.status];
      const newKey = statusCountKeys[newStatus];

      return {
        ...currentCounts,
        ...(oldKey
          ? { [oldKey]: Math.max(0, Number(currentCounts[oldKey] || 0) - 1) }
          : {}),
        ...(newKey ? { [newKey]: Number(currentCounts[newKey] || 0) + 1 } : {}),
      };
    });
  };

  const handleLeadProductsQuickUpdated = (leadId, newProducts) => {
    setLeads((currentLeads) =>
      currentLeads.map((lead) =>
        lead.lead_id === leadId ? { ...lead, products: newProducts } : lead,
      ),
    );
  };

  const handlePanelClose = () => {
    setSelectedLeadId(null);
    fetchLeads(filters, page, false);
    fetchCounts(filters);
  };

  const toggleColumn = (column) => {
    setVisibleColumns((current) => ({
      ...current,
      [column]: !current[column],
    }));
  };

  const getStatusCount = (status) => {
    if (status === "all") return counts.total;
    const keys = {
      New: "new_count",
      Contacted: "contacted_count",
      Qualified: "qualified_count",
      "Demo Scheduled": "demo_scheduled_count",
      "Demo Completed": "demo_completed_count",
      Won: "won_count",
      Lost: "lost_count",
    };
    return Number(counts[keys[status]] || 0);
  };

  return (
    <Layout title="My Leads" subtitle="Manage and track your leads">
      <div className="my-leads-page">
        <div className="partner-leads-header">
          <div className="partner-status-tabs">
            {STATUS_TABS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                className={`partner-status-tab ${filters.status === tab.key ? "active" : ""}`}
                onClick={() => handleStatusTab(tab.key)}
              >
                <span>{tab.label}</span>
                <strong>{countsLoading ? "…" : getStatusCount(tab.key)}</strong>
              </button>
            ))}
          </div>

          <div className="partner-leads-toolbar">
            <ExportButton getExportData={() => api.exportLeads(filters)} />

            {ownerCanFilterSalesRep && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowBulkImport(true)}
              >
                <i className="fas fa-file-upload"></i> Bulk Import
              </button>
            )}

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate("/leads/add")}
            >
              <i className="fas fa-plus"></i> Add Lead
            </button>
          </div>
        </div>

        <LeadFilters
          onFilterChange={handleFilterChange}
          activeStatus={filters.status}
          userRole={userRole}
        />

        {error && (
          <div className="error-message" role="alert">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        {countsError && (
          <div className="error-message" role="alert">
            {countsError}
          </div>
        )}

        <div className="my-leads-data-scroll">
          {loading && leads.length === 0 ? (
            <div className="my-leads-table-loading">
              <LoadingSpinner message="Loading leads..." />
            </div>
          ) : (
            <div className="leads-table-wrapper">
              <LeadTable
                leads={leads}
                onBookDemo={handleBookDemo}
                onView={handleViewLead}
                onEdit={handleEditLead}
                onLeadQuickUpdated={handleLeadQuickUpdated}
                onLeadProductsQuickUpdated={handleLeadProductsQuickUpdated}
                onAddActivity={handleAddActivity}
                userRole={userRole}
                visibleColumns={visibleColumns}
                productCatalog={productCatalog}
              />
            </div>
          )}

          {loading && leads.length > 0 && (
            <div className="lead-list-refreshing" aria-live="polite">
              <i className="fas fa-spinner fa-spin"></i> Refreshing leads...
            </div>
          )}

          {hasMore ? (
            <div className="my-leads-load-more">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Loading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-chevron-down"></i> Load More
                  </>
                )}
              </button>
            </div>
          ) : leads.length > 0 ? (
            <div className="my-leads-end-message">Showing all loaded leads</div>
          ) : null}
        </div>
      </div>

      {showBulkImport && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: "900px" }}>
            <BulkImport
              onComplete={() => {
                setShowBulkImport(false);
                fetchLeads(filters, 1, false);
                fetchCounts(filters);
              }}
              onCancel={() => setShowBulkImport(false)}
            />
          </div>
        </div>
      )}

      {selectedLeadId && (
        <ViewLead
          leadId={selectedLeadId}
          embedded
          onClose={handlePanelClose}
          onUpdated={handleLeadUpdated}
        />
      )}
    </Layout>
  );
}

export default LeadList;
